import {
  flagProp,
  normalizeExerciseName,
  numberProp,
  templateCycle,
  templateSchedule,
  textProp,
  workoutExercises,
  type AutoLine,
  type ExerciseNode,
  type SetLine,
  type TemplateNode,
  type WorkoutItem,
  type WorkoutNode,
} from './ast'
import { ScriptError, type Dim } from './lexer'
import { Builtins, Interpreter } from './interpreter'
import { NIL, Scope, arr, bool, num, obj, txt, type Value } from './values'

/**
 * A template and your history into a plan. Pure: same script and same history, same plan, on
 * every device and in the corpus.
 */

/** What a set asks for. A number that was written as a range keeps both ends. */
export interface Target {
  readonly repsLow?: number | undefined
  readonly repsHigh?: number | undefined
  readonly repsOpen?: boolean | undefined
  readonly weightKg?: number | undefined
  readonly weightKgHigh?: number | undefined
  readonly secondsLow?: number | undefined
  readonly secondsHigh?: number | undefined
  readonly metersLow?: number | undefined
  readonly metersHigh?: number | undefined
}

export interface PlannedSet {
  readonly exercise: string
  /** Which line of the exercise made this set, and where in that line's output it sits. */
  readonly line: number
  readonly place: number
  readonly warmup: boolean
  readonly target: Target
  readonly restSeconds?: number | undefined
  readonly video: boolean
  readonly notes?: string | undefined
}

export interface PlannedExercise {
  readonly name: string
  readonly snapKg?: number | undefined
  readonly notes?: string | undefined
  readonly sets: readonly PlannedSet[]
  /** What went wrong working this exercise out. It plans no sets, and says why. */
  readonly error?: string | undefined
  /** Every alternative of the `choose` this came from, planned whole, itself included. */
  readonly alternatives: readonly PlannedExercise[]
  /** Shared by the members of one superset, whose sets are performed in rounds. */
  readonly superset?: number | undefined
}

export interface WorkoutPlan {
  readonly workout: string
  readonly exercises: readonly PlannedExercise[]
}

/** One set as it was logged, or as it was planned and passed over. */
export interface HistSet {
  readonly exercise: string
  readonly reps?: number | undefined
  readonly weightKg?: number | undefined
  readonly seconds?: number | undefined
  readonly meters?: number | undefined
  readonly rpe?: number | undefined
  readonly warmup?: boolean | undefined
  readonly logged?: boolean | undefined
  readonly target?: Target | undefined
}

export interface HistSession {
  readonly workout: string
  readonly sets: readonly HistSet[]
}

/**
 * Everything evaluation is allowed to look at: the sessions of the current enrollment, oldest
 * first, and every set ever logged of the exercises the template names.
 */
export interface History {
  readonly sessions: readonly HistSession[]
  readonly allSets: Readonly<Record<string, readonly HistSet[]>>
}

export const EMPTY_HISTORY: History = { sessions: [], allSets: {} }

/**
 * The step each exercise's weight rounds to, keyed by its normalised name, for exercises whose
 * lines do not say. It is an input like history rather than something the language looks up:
 * what a barbell moves in is knowledge about the world, and the world is the runtime's to know.
 */
export type Snaps = Readonly<Record<string, number>>

export const NO_SNAPS: Snaps = {}

function spanValue(
  low: number | undefined,
  high: number | undefined,
  open: boolean,
  dim: Dim,
): Value {
  return low === undefined ? NIL : { kind: 'span', low, high: high ?? low, open, dim }
}

function targetValue(target: Target): Value {
  return obj('A target', {
    reps: spanValue(target.repsLow, target.repsHigh, target.repsOpen ?? false, 'none'),
    weight: spanValue(target.weightKg, target.weightKgHigh, false, 'weight'),
    duration: spanValue(target.secondsLow, target.secondsHigh, false, 'time'),
    distance: spanValue(target.metersLow, target.metersHigh, false, 'distance'),
  })
}

/**
 * What surrounds a set from further out than the set itself: what the exercise said, and then
 * what the line said. Each layer stands in for whatever the layer inside it leaves unsaid.
 */
interface Around {
  readonly rest: number | null
  readonly video: boolean
  readonly notes?: string | undefined
  readonly warmup: boolean
}

/** True when the set met the low end of everything it was asked for. */
export function cleared(set: HistSet): boolean {
  if (set.logged === false) {
    return false
  }
  const asked = set.target
  if (!asked) {
    return true
  }
  if (asked.repsLow !== undefined && (set.reps ?? 0) < asked.repsLow) return false
  if (asked.weightKg !== undefined && (set.weightKg ?? 0) < asked.weightKg) return false
  if (asked.secondsLow !== undefined && (set.seconds ?? 0) < asked.secondsLow) return false
  if (asked.metersLow !== undefined && (set.meters ?? 0) < asked.metersLow) return false
  return true
}

function setValue(set: HistSet): Value {
  return obj('A set', {
    exercise: txt(set.exercise),
    reps: set.reps === undefined ? NIL : num(set.reps),
    weight: set.weightKg === undefined ? NIL : num(set.weightKg, 'weight'),
    duration: set.seconds === undefined ? NIL : num(set.seconds, 'time'),
    distance: set.meters === undefined ? NIL : num(set.meters, 'distance'),
    rpe: set.rpe === undefined ? NIL : num(set.rpe),
    warmup: bool(set.warmup ?? false),
    logged: bool(set.logged ?? true),
    target: set.target ? targetValue(set.target) : NIL,
    cleared: bool(cleared(set)),
  })
}

function sessionValue(workout: string, sets: readonly HistSet[]): Value {
  return obj('A session', { workout: txt(workout), sets: arr(sets.map(setValue)) })
}

/** Epley, the same estimate the exercise page uses. Warm-ups are not performances. */
function estimateOneRepMax(sets: readonly HistSet[]): number | null {
  const estimates = sets
    .filter((set) => (set.logged ?? true) && !(set.warmup ?? false))
    .map((set) => {
      const weight = set.weightKg
      const reps = set.reps
      if (weight === undefined || reps === undefined || weight <= 0 || reps <= 0) {
        return null
      }
      return weight * (1 + reps / 30)
    })
    .filter((value): value is number => value !== null)
  return estimates.length === 0 ? null : Math.max(...estimates)
}

export class Evaluator {
  private readonly template: TemplateNode
  private readonly history: History
  /** The workout being played as it stands, so a block can react to what just happened. */
  private readonly current: HistSession | null
  private readonly snaps: Snaps

  constructor(
    template: TemplateNode,
    history: History = EMPTY_HISTORY,
    current: HistSession | null = null,
    snaps: Snaps = NO_SNAPS,
  ) {
    this.template = template
    this.history = history
    this.current = current
    this.snaps = snaps
  }

  workout(name: string): WorkoutPlan | null {
    const node = this.template.workouts.find((workout) => workout.name === name)
    if (!node) {
      return null
    }
    return {
      workout: node.name,
      exercises: node.items.flatMap((item, index) => this.plan(node, index, item)),
    }
  }

  private plan(workout: WorkoutNode, index: number, item: WorkoutItem): PlannedExercise[] {
    if (item.type === 'exercise') {
      const planned = this.exercise(workout, item.exercise)
      return planned.sets.length > 0 || planned.error !== undefined ? [planned] : []
    }

    if (item.kind === 'superset') {
      // The members play in rounds — one set of each in turn — and the group's rest lands on
      // the set that opens each round after the first. Nothing else in a superset carries
      // rest; the parser refuses it.
      const restSeconds = numberProp(item.props, 'rest')
      const members = item.exercises
        .map((exercise) => this.exercise(workout, exercise))
        .filter((planned) => planned.sets.length > 0 || planned.error !== undefined)
      const sets = members.map((planned) => [...planned.sets])
      if (restSeconds !== null) {
        const rounds = Math.max(0, ...sets.map((held) => held.length))
        for (let round = 1; round < rounds; round++) {
          // The rest before a round lands on whichever member opens it.
          const opener = sets.find((held) => held.length > round)
          const set = opener?.[round]
          if (opener !== undefined && set !== undefined) {
            opener[round] = { ...set, restSeconds: Math.trunc(restSeconds) }
          }
        }
      }
      return members.map((planned, member) => ({
        ...planned,
        sets: sets[member] ?? planned.sets,
        superset: index,
      }))
    }

    if (item.kind === 'choose') {
      // Every alternative is planned whole and carried along, so the workout can swap between
      // them with nothing left to work out — and no connection needed.
      const planned = item.exercises
        .map((exercise) => this.exercise(workout, exercise))
        .filter((held) => held.sets.length > 0 || held.error !== undefined)
      const alternatives = planned.filter(
        (held) => held.error === undefined && held.sets.length > 0,
      )
      const first = planned[0]
      return first ? [{ ...first, alternatives }] : []
    }

    // One per session, taking the next after the one planned last time.
    const done = this.history.sessions.filter((session) => session.workout === workout.name).length
    const picked = item.exercises[done % item.exercises.length]
    if (picked === undefined) {
      return []
    }
    const planned = this.exercise(workout, picked)
    return planned.sets.length > 0 || planned.error !== undefined ? [planned] : []
  }

  private exercise(workout: WorkoutNode, node: ExerciseNode): PlannedExercise {
    const key = normalizeExerciseName(node.name)
    // The line says it, or the runtime knows it for that name, or the weight rounds to nothing.
    const snap = numberProp(node.props, 'snap') ?? this.snaps[key] ?? null
    const notes = textProp(node.props, 'notes')
    // What the exercise says goes for every line of it that does not say otherwise.
    const held: Around = {
      rest: numberProp(node.props, 'rest'),
      video: flagProp(node.props, 'video'),
      warmup: false,
    }

    const mine = this.history.sessions
      .map(
        (session) =>
          [
            session,
            session.sets.filter((set) => normalizeExerciseName(set.exercise) === key),
          ] as const,
      )
      .filter(([, sets]) => sets.length > 0)

    const roots = new Map<string, Value>([
      ['sessions', arr(mine.map(([session, sets]) => sessionValue(session.workout, sets)))],
      ['session', sessionValue(this.current?.workout ?? workout.name, this.current?.sets ?? [])],
      ['true', bool(true)],
      ['false', bool(false)],
      ['null', NIL],
    ])
    const builtins = new Builtins(
      (name) => (this.history.allSets[name] ?? []).map(setValue),
      (name) => estimateOneRepMax(this.history.allSets[name] ?? []),
      node.name,
    )
    const interpreter = new Interpreter(roots, builtins)

    const sets: PlannedSet[] = []
    const ramps: [number, AutoLine][] = []
    try {
      node.lines.forEach((line, index) => {
        if (line.type === 'set') {
          sets.push(
            ...this.build(
              interpreter,
              new Scope(),
              node.name,
              line,
              index,
              sets.length,
              held,
              snap,
            ),
          )
        } else if (line.type === 'auto') {
          ramps.push([index, line])
        } else {
          // A block carries nothing of its own; each line it yields speaks for itself.
          let place = 0
          interpreter.run(line.body, new Scope(), (yielded, scope) => {
            sets.push(
              ...this.build(interpreter, scope, node.name, yielded, index, place, held, snap),
            )
            place += 1
          })
        }
      })
    } catch (error) {
      if (error instanceof ScriptError) {
        return {
          name: node.name,
          snapKg: snap ?? undefined,
          notes: notes ?? undefined,
          sets: [],
          error: error.located,
          alternatives: [],
        }
      }
      throw error
    }

    // A ramp needs the weight of the first working set, which a later line may compute.
    const working = sets.find((set) => !set.warmup && set.target.weightKg !== undefined)?.target
      .weightKg
    for (const [index, ramp] of [...ramps].reverse()) {
      let warmups: PlannedSet[]
      try {
        warmups = this.expand(interpreter, ramp, node.name, index, working ?? null, held, snap)
      } catch (error) {
        if (error instanceof ScriptError) {
          return {
            name: node.name,
            snapKg: snap ?? undefined,
            notes: notes ?? undefined,
            sets: [],
            error: error.located,
            alternatives: [],
          }
        }
        throw error
      }
      const before = sets.filter((set) => set.line < index).length
      sets.splice(before, 0, ...warmups)
    }

    return {
      name: node.name,
      snapKg: snap ?? undefined,
      notes: notes ?? undefined,
      sets,
      alternatives: [],
    }
  }

  private build(
    interpreter: Interpreter,
    scope: Scope,
    exercise: string,
    line: SetLine,
    index: number,
    place: number,
    around: Around,
    snap: number | null,
  ): PlannedSet[] {
    const countValue = line.count ? interpreter.eval(line.count, scope) : null
    const count = countValue?.kind === 'number' ? Math.trunc(countValue.value) : 1
    const primary = interpreter.eval(line.primary, scope)

    let target: Target
    if (primary.kind === 'number') {
      if (primary.dim === 'weight') {
        throw new ScriptError(
          line.at.line,
          line.at.column,
          'A set asks for reps, a duration or a distance — not a weight',
        )
      }
      target =
        primary.dim === 'none'
          ? { repsLow: primary.value, repsHigh: primary.value }
          : primary.dim === 'time'
            ? { secondsLow: primary.value, secondsHigh: primary.value }
            : { metersLow: primary.value, metersHigh: primary.value }
    } else if (primary.kind === 'span') {
      if (primary.dim === 'weight') {
        throw new ScriptError(
          line.at.line,
          line.at.column,
          'A set asks for reps, a duration or a distance — not a weight',
        )
      }
      target =
        primary.dim === 'none'
          ? { repsLow: primary.low, repsHigh: primary.high, repsOpen: primary.open }
          : primary.dim === 'time'
            ? { secondsLow: primary.low, secondsHigh: primary.high }
            : { metersLow: primary.low, metersHigh: primary.high }
    } else {
      throw new ScriptError(
        line.at.line,
        line.at.column,
        'A set asks for reps, a duration or a distance',
      )
    }

    // `rpe` in a weight is asking about the reps of the line it sits on.
    interpreter.repsInHand =
      target.repsLow !== undefined && target.repsHigh === target.repsLow && !target.repsOpen
        ? target.repsLow
        : null
    let weight: number | undefined
    let weightHigh: number | undefined
    if (line.weight) {
      const value = interpreter.eval(line.weight, scope)
      if (value.kind === 'number' && value.dim === 'weight') {
        weight = value.value
      } else if (value.kind === 'span' && value.dim === 'weight' && !value.open) {
        weight = value.low
        weightHigh = value.high
      } else {
        throw new ScriptError(
          line.weight.at.line,
          line.weight.at.column,
          'A set is performed with a weight',
        )
      }
    }
    interpreter.repsInHand = null

    const toSnap = (held: number | undefined): number | undefined =>
      held !== undefined && snap !== null && snap > 0 ? Math.round(held / snap) * snap : held
    const snapped = toSnap(weight)
    const snappedHigh = toSnap(weightHigh)
    const restValue = line.mods.rest ? interpreter.eval(line.mods.rest, scope) : null
    const rest = restValue?.kind === 'number' ? Math.trunc(restValue.value) : (around.rest ?? null)

    return Array.from({ length: Math.max(count, 0) }, (_, offset) => ({
      exercise,
      line: index,
      place: place + offset,
      warmup: line.warmup || around.warmup,
      target: { ...target, weightKg: snapped, weightKgHigh: snappedHigh },
      restSeconds: rest ?? undefined,
      video: line.mods.video ?? around.video,
      notes: line.mods.notes ?? around.notes,
    }))
  }

  /**
   * How many reps a ramp set asks for when the ramp did not say: fewer the nearer it gets to
   * the weight it is warming up for. An empty bar is a rehearsal and a near-working single is
   * a last rehearsal, and a list of numbers cannot tell one ramp from another — five sets from
   * the bar to a heavy squat and two sets to a light one both get the same list, and one of
   * them is wrong. The distance says it instead, so a ramp is right at any length.
   */
  private rampReps(weight: number, working: number): number {
    const near = weight / working
    if (near < 0.6) return 5
    if (near < 0.75) return 3
    if (near < 0.85) return 2
    return 1
  }

  /** The ramp: from the bar upward, in steps, until it reaches its share of the working weight. */
  private expand(
    interpreter: Interpreter,
    ramp: AutoLine,
    exercise: string,
    index: number,
    working: number | null,
    around: Around,
    snap: number | null,
  ): PlannedSet[] {
    if (working === null || working <= 0) {
      return []
    }
    const scope = new Scope()
    const arg = (name: string): number | null => {
      const found = ramp.args.find(([key]) => key === name)
      if (!found) {
        return null
      }
      const value = interpreter.eval(found[1], scope)
      return value.kind === 'number' ? value.value : null
    }

    const bar = arg('bar') ?? 20
    const jump = arg('jump') ?? 20
    const stop = arg('to') ?? 0.9
    const repsArg = ramp.args.find(([key]) => key === 'reps')
    const repsValue = repsArg ? interpreter.eval(repsArg[1], scope) : null
    // Null where the ramp said nothing, which is not the same as an empty list: nothing said is
    // what hands the question over to how near each set is to the weight it is warming up for.
    // Anything in the list that is not a number is not a count of reps and is passed over —
    // a ramp set asking for zero reps is not a set anybody can do.
    const reps =
      repsValue?.kind === 'array'
        ? repsValue.items.flatMap((item) => (item.kind === 'number' ? [item.value] : []))
        : null
    if (jump <= 0) {
      throw new ScriptError(
        ramp.at.line,
        ramp.at.column,
        'A ramp climbs in steps bigger than nothing',
      )
    }

    // Whatever the ramp asks for around its sets, every set it expands to carries.
    const restValue = ramp.mods.rest ? interpreter.eval(ramp.mods.rest, scope) : null
    const rest = restValue?.kind === 'number' ? Math.trunc(restValue.value) : (around.rest ?? null)
    const video = ramp.mods.video ?? around.video
    const notes = ramp.mods.notes ?? around.notes

    const sets: PlannedSet[] = []
    let weight = bar
    let place = 0
    while (weight <= working * stop && place < 12) {
      const snapped = snap !== null && snap > 0 ? Math.round(weight / snap) * snap : weight
      const asked =
        reps === null
          ? this.rampReps(snapped, working)
          : (reps[place] ?? reps[reps.length - 1] ?? 1)
      sets.push({
        exercise,
        line: index,
        place,
        warmup: ramp.warmup || around.warmup,
        target: { repsLow: asked, repsHigh: asked, weightKg: snapped },
        restSeconds: rest ?? undefined,
        video,
        notes,
      })
      weight += jump
      place += 1
    }
    return sets
  }
}

/**
 * Which workout the template offers. This is the one thing that reads a calendar, and it is
 * kept apart from evaluating a workout, which never does.
 *
 * `daysIntoCycle` is how far into the cycle today is, counted from its day zero. Where day zero
 * of a run falls on a calendar is the app's business, not the language's — a script says which
 * day of the cycle a workout is on and nothing about which Monday that is.
 */
export function nextWorkout(
  template: TemplateNode,
  history: History,
  daysIntoCycle: number,
): WorkoutNode | null {
  const workouts = template.workouts
  const opening = workouts[0]
  if (opening === undefined) {
    return null
  }
  switch (templateSchedule(template)) {
    case 'free':
      return opening

    case 'sequential': {
      const last = history.sessions[history.sessions.length - 1]?.workout
      const at = workouts.findIndex((workout) => workout.name === last)
      return workouts[at < 0 ? 0 : (at + 1) % workouts.length] ?? opening
    }

    case 'strict': {
      const cycle = templateCycle(template)
      if (cycle === null) {
        return opening
      }
      const day = cycle > 0 ? ((daysIntoCycle % cycle) + cycle) % cycle : 0
      return workouts.find((workout) => numberProp(workout.props, 'day') === day) ?? null
    }
  }
}

/** What a strict schedule needs said before it can be relied on. */
export function validate(template: TemplateNode): ScriptError[] {
  const problems: ScriptError[] = []
  const strict = templateSchedule(template) === 'strict'
  const cycle = templateCycle(template)
  if (strict && (cycle === null || cycle < 1)) {
    problems.push(
      new ScriptError(
        template.at.line,
        template.at.column,
        'A strict schedule needs a cycle of at least one day',
      ),
    )
  }
  const days = new Map<number, string>()
  for (const workout of template.workouts) {
    const day = numberProp(workout.props, 'day')
    if (!strict) {
      if (day !== null) {
        problems.push(
          new ScriptError(
            workout.at.line,
            workout.at.column,
            '`day` only means something under a strict schedule',
          ),
        )
      }
      continue
    }
    if (day === null) {
      problems.push(
        new ScriptError(
          workout.at.line,
          workout.at.column,
          'Under a strict schedule every workout says which day it falls on',
        ),
      )
      continue
    }
    if (cycle !== null && day >= cycle) {
      problems.push(
        new ScriptError(
          workout.at.line,
          workout.at.column,
          `Day ${day} falls outside a cycle of ${cycle} days`,
        ),
      )
    }
    const taken = days.get(day)
    if (taken !== undefined) {
      problems.push(
        new ScriptError(workout.at.line, workout.at.column, `Day ${day} is already "${taken}"`),
      )
    }
    days.set(day, workout.name)
  }
  return problems
}

export { workoutExercises }
